import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight} from 'lucide-react';
import { useAuthStore } from '@/store';
import { apiClient } from '@/lib/api-client';
import { formatDate, formatPrice } from '@/utils';

interface TradeOffer {
  id: string;
  listing_id: string;
  offerer_id: string;
  seller_id: string;
  offerer_username: string;
  seller_username: string;
  listing_title: string;
  card_name: string;
  card_image?: string;
  offer_description: string;
  cash_included: number;
  counter_description?: string;
  counter_cash?: number;
  status: string;
  created_at: string;
  updated_at: string;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  accepted: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  declined: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  countered: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  counter_accepted: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  counter_declined: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  cancelled: 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  countered: 'Counter Offered',
  counter_accepted: 'Counter Accepted',
  counter_declined: 'Counter Declined',
  cancelled: 'Cancelled',
};

export default function TradeOffersPage() {
  const { user } = useAuthStore();
  const [offers, setOffers] = useState<TradeOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'sent' | 'received'>('all');

  const fetchOffers = async () => {
    try {
      const response = await apiClient.get<any>('/trade-offers/me');
      setOffers(response.data?.data ?? response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch trade offers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const filtered = offers.filter(o => {
    if (filter === 'sent') return o.offerer_id === user?.id;
    if (filter === 'received') return o.seller_id === user?.id;
    return true;
  });

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="bg-gradient-to-r from-purple-600 to-brand-600 text-white py-10">
        <div className="container-custom">
          <h1 className="text-3xl font-display font-bold mb-1">Trade Offers</h1>
          <p className="text-purple-100">Manage your incoming and outgoing trade offers</p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-4xl">
        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {(['all', 'sent', 'received'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ${
                filter === f ? 'bg-purple-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-32 rounded-xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <ArrowLeftRight className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-zinc-500" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No trade offers yet</h3>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">Browse trade listings to propose your first trade</p>
            <Link to="/marketplace" className="btn-primary">Browse Listings</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((offer) => {
              const isSent = offer.offerer_id === user?.id;
              return (
                <div key={offer.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
                  <div className="flex items-start gap-4">
                    {/* Card image */}
                    <div className="h-16 w-12 rounded-lg bg-gray-100 dark:bg-zinc-800 overflow-hidden flex-shrink-0">
                      {offer.card_image ? (
                        <img src={offer.card_image} alt={offer.card_name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-2xl">🃏</div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                        <div>
                          <Link to={`/listings/${offer.listing_id}`} className="font-semibold text-sm hover:text-brand-600 dark:text-brand-400 transition-colors">
                            {offer.card_name ?? offer.listing_title}
                          </Link>
                          <p className="text-xs text-gray-500 dark:text-zinc-500">
                            {isSent ? `To: ${offer.seller_username}` : `From: ${offer.offerer_username}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[offer.status] ?? 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500'}`}>
                            {STATUS_LABELS[offer.status] ?? offer.status}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-zinc-500">{formatDate(offer.updated_at, 'MMM d, yyyy')}</span>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 dark:text-zinc-300 mb-1">{offer.offer_description}</p>
                      {offer.cash_included > 0 && (
                        <p className="text-xs text-green-600 font-medium">+ {formatPrice(offer.cash_included)} cash</p>
                      )}

                      {offer.counter_description && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-zinc-800">
                          <p className="text-xs font-medium text-gray-500 dark:text-zinc-500 mb-1">Counter offer:</p>
                          <p className="text-sm text-gray-700 dark:text-zinc-300">{offer.counter_description}</p>
                          {offer.counter_cash && offer.counter_cash > 0 && (
                            <p className="text-xs text-green-600 font-medium mt-0.5">+ {formatPrice(offer.counter_cash)} cash</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}